import React, { createContext, useContext, useState, useCallback } from "react";

export type Locale = "en" | "pt-br";

const translations = {
  // ── Common ──
  "loading": { en: "Loading...", "pt-br": "Carregando..." },
  "select": { en: "Select...", "pt-br": "Selecione..." },
  "selectCountry": { en: "Select country...", "pt-br": "Selecione o país..." },
  "selectReason": { en: "Select reason...", "pt-br": "Selecione o motivo..." },
  "yes": { en: "Yes", "pt-br": "Sim" },
  "no": { en: "No", "pt-br": "Não" },
  "document": { en: "Document", "pt-br": "Documento" },
  "remove": { en: "Remove", "pt-br": "Remover" },
  "open": { en: "Open", "pt-br": "Abrir" },
  "download": { en: "Download", "pt-br": "Baixar" },
  "view": { en: "View", "pt-br": "Ver" },

  // ── Header / Page ──
  "pageTitle": { en: "Business Verification (KYB)", "pt-br": "Verificação Empresarial (KYB)" },
  "pageSubtitle": { en: "Please complete the form below to verify your business. Your progress is saved automatically as a draft.", "pt-br": "Por favor, preencha o formulário abaixo para verificar sua empresa. Seu progresso é salvo automaticamente como rascunho." },

  // ── Session Menu ──
  "status": { en: "Status", "pt-br": "Status" },
  "submitted": { en: "Submitted", "pt-br": "Enviado" },
  "changesSaved": { en: "Changes saved", "pt-br": "Alterações salvas" },
  "draft": { en: "Draft", "pt-br": "Rascunho" },
  "session": { en: "Session", "pt-br": "Sessão" },
  "customerId": { en: "Customer ID", "pt-br": "ID do Cliente" },
  "newSession": { en: "+ New Session", "pt-br": "+ Nova Sessão" },
  "newSessionConfirm": { en: "All the info will be lost. Do you want to proceed?", "pt-br": "Todas as informações serão perdidas. Deseja continuar?" },

  // ── Submitted Screen ──
  "applicationSubmitted": { en: "Application Submitted", "pt-br": "Solicitação Enviada" },
  "submittedMessage": { en: "Thank you for submitting your business verification application. Our team will review your information and documents shortly.", "pt-br": "Obrigado por enviar sua solicitação de verificação empresarial. Nossa equipe analisará suas informações e documentos em breve." },
  "reference": { en: "Reference", "pt-br": "Referência" },
  "submittedNote": { en: "You will be contacted if any additional information is needed.", "pt-br": "Entraremos em contato caso informações adicionais sejam necessárias." },
  "submittedSuccess": { en: "Application submitted successfully!", "pt-br": "Solicitação enviada com sucesso!" },
  "submitFailed": { en: "Failed to submit", "pt-br": "Falha ao enviar" },
  "staleFormReloaded": { en: "This form was updated in another tab. The latest saved data was reloaded.", "pt-br": "Este formulário foi atualizado em outra aba. Os dados salvos mais recentes foram recarregados." },

  // ── Section: Company Data ──
  "companyData": { en: "Company Data", "pt-br": "Dados da Empresa" },
  "companyDataDesc": { en: "Essential details about your business.", "pt-br": "Detalhes essenciais sobre sua empresa." },
  "legalName": { en: "Legal Name", "pt-br": "Razão Social" },
  "country": { en: "Country", "pt-br": "País" },
  "doingBusinessAs": { en: "Doing Business As", "pt-br": "Nome Fantasia" },
  "incorporationDate": { en: "Incorporation Date", "pt-br": "Data de Constituição" },
  "email": { en: "Email", "pt-br": "E-mail" },
  "taxId": { en: "Tax ID", "pt-br": "CNPJ" },
  "ownerTaxId": { en: "CNPJ/CPF", "pt-br": "CNPJ/CPF" },
  "phone": { en: "Phone", "pt-br": "Telefone" },
  "website": { en: "Website", "pt-br": "Website" },
  "noWebsite": { en: "No website", "pt-br": "Sem website" },

  // ── Placeholders ──
  "phLegalName": { en: "Company S.A.", "pt-br": "Empresa S.A." },
  "phDoingBusinessAs": { en: "Trade name or services", "pt-br": "Nome comercial ou serviços" },
  "phEmail": { en: "hello@company.com", "pt-br": "contato@empresa.com" },
  "phTaxId": { en: "42.887.120/0001-00", "pt-br": "42.887.120/0001-00" },
  "phPhone": { en: "+55 11 99999-9999", "pt-br": "+55 11 99999-9999" },
  "phWebsite": { en: "https://company.com", "pt-br": "https://empresa.com" },
  "phAddress": { en: "123 Main St, Suite 100", "pt-br": "Rua Principal, 123, Sala 100" },
  "phCity": { en: "São Paulo", "pt-br": "São Paulo" },
  "phState": { en: "SP", "pt-br": "SP" },
  "phPostalCode": { en: "01310-100", "pt-br": "01310-100" },
  "phFullName": { en: "Full legal name", "pt-br": "Nome completo" },
  "phOccupation": { en: "CEO, Director, etc.", "pt-br": "CEO, Diretor, etc." },
  "phOwnerEmail": { en: "owner@company.com", "pt-br": "socio@empresa.com" },
  "phOwnerTaxId": { en: "123.456.789-00", "pt-br": "123.456.789-00" },
  "phOwnership": { en: "25", "pt-br": "25" },
  "phOwnerAddress": { en: "123 Main St", "pt-br": "Rua Principal, 123" },
  "phOwnerCity": { en: "City", "pt-br": "Cidade" },
  "phOwnerState": { en: "State", "pt-br": "Estado" },
  "phAnnualRevenue": { en: "1,000,000", "pt-br": "1.000.000" },
  "phMonthlyTxVolume": { en: "500,000", "pt-br": "500.000" },
  "phBusinessDesc": { en: "Describe the services your company provides...", "pt-br": "Descreva os serviços que sua empresa oferece..." },
  "phMonthlyVolume": { en: "100,000", "pt-br": "100.000" },
  "phPerTxVolume": { en: "50,000", "pt-br": "50.000" },

  // ── No Website Reasons ──
  "underDevelopment": { en: "Under development", "pt-br": "Em desenvolvimento" },
  "notApplicable": { en: "Not applicable to this business", "pt-br": "Não aplicável a este negócio" },
  "confidential": { en: "Confidential / not public", "pt-br": "Confidencial / não público" },
  "socialMediaOnly": { en: "Social media presence only", "pt-br": "Apenas presença em redes sociais" },

  // ── Section: Physical Address ──
  "physicalAddress": { en: "Physical Address", "pt-br": "Endereço Físico" },
  "physicalAddressDesc": { en: "Please enter your business physical address.", "pt-br": "Insira o endereço físico da sua empresa." },
  "addressLine1": { en: "Address Line 1", "pt-br": "Endereço" },
  "city": { en: "City", "pt-br": "Cidade" },
  "stateProvince": { en: "State / Province", "pt-br": "Estado / Província" },
  "postalCode": { en: "Postal Code", "pt-br": "CEP" },

  // ── Section: Business Owners ──
  "businessOwners": { en: "List of Business Owners", "pt-br": "Lista de Sócios" },
  "businessOwnersDesc": { en: "We are required to verify the identity of all beneficial owners and one controlling partner of your business.", "pt-br": "Precisamos verificar a identidade de todos os beneficiários finais e um sócio controlador da sua empresa." },
  "addOwner": { en: "+ Add Owner", "pt-br": "+ Adicionar Sócio" },
  "ownerNum": { en: "Owner", "pt-br": "Sócio" },
  "role": { en: "Role", "pt-br": "Função" },
  "ubo": { en: "UBO (Ultimate Beneficial Owner)", "pt-br": "UBO (Beneficiário Final)" },
  "shareholder": { en: "Shareholder", "pt-br": "Acionista" },
  "representative": { en: "Representative", "pt-br": "Representante" },
  "fullName": { en: "Full Name", "pt-br": "Nome Completo" },
  "dateOfBirth": { en: "Date of Birth", "pt-br": "Data de Nascimento" },
  "titleOccupation": { en: "Title / Occupation", "pt-br": "Cargo / Ocupação" },
  "ownershipPct": { en: "Ownership %", "pt-br": "Participação %" },
  "address": { en: "Address", "pt-br": "Endereço" },
  "state": { en: "State", "pt-br": "Estado" },

  // ── Owner ID Document ──
  "idDocument": { en: "ID Document", "pt-br": "Documento de Identidade" },
  "countryOfIssue": { en: "Country of Issue", "pt-br": "País de Emissão" },
  "documentType": { en: "Document Type", "pt-br": "Tipo de Documento" },
  "idCard": { en: "ID Card", "pt-br": "RG / Carteira de Identidade" },
  "passport": { en: "Passport", "pt-br": "Passaporte" },
  "driversLicense": { en: "Driver's License", "pt-br": "CNH" },
  "idDocSingleSide": { en: "My ID document has only one side", "pt-br": "Meu documento tem apenas um lado" },
  "documentFront": { en: "Document Front", "pt-br": "Frente do Documento" },
  "documentBack": { en: "Document Back", "pt-br": "Verso do Documento" },

  // ── Owner Proof of Address ──
  "proofOfAddress": { en: "Proof of Address", "pt-br": "Comprovante de Endereço" },
  "bankAccountStatement": { en: "Bank Account Statement (last 90 days)", "pt-br": "Extrato da Conta Bancária (últimos 90 dias)" },
  "noBankAccountStatementUploaded": { en: "No bank account statement uploaded.", "pt-br": "Nenhum extrato da conta bancária enviado." },
  "ownerBankAccountStatementRequired": { en: "Owner bank account statement is required", "pt-br": "Extrato da conta bancária do sócio é obrigatório" },

  // ── Section: Documents ──
  "documents": { en: "Documents", "pt-br": "Documentos" },
  "documentsDesc": { en: "Please provide all documents related to this business.", "pt-br": "Forneça todos os documentos relacionados à sua empresa." },
  "incorporation": { en: "Incorporation", "pt-br": "Constituição" },
  "incorporationDesc": { en: "Upload the articles of incorporation, certificate of formation, or equivalent founding document.", "pt-br": "Envie o contrato social, ato constitutivo ou documento de constituição equivalente." },
  "proofOfOwnership": { en: "Proof of Ownership", "pt-br": "Comprovante de Propriedade" },
  "proofOfOwnershipDesc": { en: "Shareholder registry, operating agreement, or other document proving ownership structure.", "pt-br": "Quadro societário, acordo operacional ou documento que comprove a estrutura de propriedade." },
  "proofOfAddressDoc": { en: "Proof of Address", "pt-br": "Comprovante de Endereço" },
  "proofOfAddressDocDesc": { en: "Bank statement, utility bill, or other document confirming business address.", "pt-br": "Extrato bancário, conta de serviços ou documento que confirme o endereço da empresa." },
  "corporateStructureChart": { en: "Corporate Structure Chart", "pt-br": "Organograma Societário" },
  "corporateStructureChartDesc": { en: "Upload the corporate structure chart showing the ownership hierarchy.", "pt-br": "Envie o organograma societário mostrando a estrutura de propriedade." },
  "signedBalanceSheet": { en: "Signed Balance Sheet", "pt-br": "Balanço Patrimonial Assinado" },
  "signedBalanceSheetDesc": { en: "Upload the most recent signed balance sheet.", "pt-br": "Envie o balanço patrimonial assinado mais recente." },
  "lastInvoice": { en: "Last Invoice", "pt-br": "Última Invoice" },
  "lastInvoiceDesc": { en: "Upload the most recent invoice issued by the business.", "pt-br": "Envie a invoice mais recente emitida pela empresa." },
  "invoiceType": { en: "Invoice Type", "pt-br": "Tipo de Invoice" },
  "selectInvoiceType": { en: "Select invoice type", "pt-br": "Selecione o tipo de invoice" },
  "invoiceTypeProduct": { en: "Product", "pt-br": "Produto" },
  "invoiceTypeService": { en: "Service", "pt-br": "Serviço" },
  "invoiceDocument": { en: "Invoice", "pt-br": "Invoice" },
  "invoiceDocumentDesc": { en: "Upload the most recent invoice issued by the business.", "pt-br": "Envie a invoice mais recente emitida pela empresa." },
  "invoiceContract": { en: "Contract", "pt-br": "Contrato" },
  "invoiceContractDesc": { en: "Optionally upload the contract related to the service invoice.", "pt-br": "Opcionalmente, envie o contrato relacionado à invoice de serviço." },
  "regulatedActivityDocument": { en: "Regulated Activity Document", "pt-br": "Documento de Atividade Regulada" },
  "regulatedActivityDocDesc": { en: "Upload documentation related to your regulated activity (e.g., license, registration).", "pt-br": "Envie a documentação relacionada à sua atividade regulada (ex.: licença, registro)." },
  "additionalDocuments": { en: "Additional Documents (Optional)", "pt-br": "Documentos Adicionais (Opcional)" },
  "additionalDocumentsDesc": { en: "Income statement, power of attorney, or other supporting documents.", "pt-br": "Demonstração de resultados, procuração ou outros documentos de suporte." },
  "signedIncomeStatement": { en: "Signed Income Statement", "pt-br": "DRE Assinada" },
  "powerOfAttorney": { en: "Power of Attorney", "pt-br": "Procuração" },
  "other": { en: "Other", "pt-br": "Outros" },

  // ── Section: Financial Information ──
  "financialInformation": { en: "Financial Information", "pt-br": "Informações Financeiras" },
  "financialInformationDesc": { en: "Tell us about the financial profile of your business.", "pt-br": "Conte-nos sobre o perfil financeiro da sua empresa." },
  "companyType": { en: "Company Type", "pt-br": "Tipo de Empresa" },
  "businessDescription": { en: "Business Description", "pt-br": "Descrição do Negócio" },
  "annualRevenue": { en: "Annual Revenue (USD)", "pt-br": "Receita Anual (USD)" },
  "monthlyTransactionVolume": { en: "Monthly Transaction Volume", "pt-br": "Volume Mensal de Transações" },
  "isRegulatedActivity": { en: "Is Regulated Activity?", "pt-br": "É Atividade Regulada?" },
  "regulatedActivityDetails": { en: "Regulated Activity Details", "pt-br": "Detalhes da Atividade Regulada" },

  // ── Section: Source of Funds ──
  "sourceOfFunds": { en: "Source of Funds", "pt-br": "Origem dos Recursos" },
  "sourceOfFundsDesc": { en: "Provide details about the source of funds for your business operations.", "pt-br": "Forneça detalhes sobre a origem dos recursos das operações da sua empresa." },
  "monthlyVolumeUsd": { en: "Monthly Volume (USD)", "pt-br": "Volume Mensal (USD)" },
  "perTransactionVolumeUsd": { en: "Per-Transaction Volume (USD)", "pt-br": "Volume por Transação (USD)" },
  "jurisdictions": { en: "Jurisdictions", "pt-br": "Jurisdições" },
  "involvedActivities": { en: "Involved Activities", "pt-br": "Atividades Envolvidas" },
  "transactionCounterparties": { en: "Transaction Counterparties", "pt-br": "Contrapartes das Transações" },

  // ── Submit Button ──
  "update": { en: "Update", "pt-br": "Atualizar" },
  "submitApplication": { en: "Submit Application", "pt-br": "Enviar Solicitação" },

  // ── Upload ──
  "uploading": { en: "Uploading...", "pt-br": "Enviando..." },
  "clickToUpload": { en: "Click here", "pt-br": "Clique aqui" },
  "orDragToUpload": { en: "or drag file to upload", "pt-br": "ou arraste o arquivo para enviar" },
  "uploadHint": { en: "JPG, PNG, PDF — max 50MB", "pt-br": "JPG, PNG, PDF — máx 50MB" },

  // ── Validation Messages ──
  "requiredField": { en: "is required", "pt-br": "é obrigatório" },
  "websiteRequired": { en: "Website is required", "pt-br": "Website é obrigatório" },
  "atLeastOneOwner": { en: "At least one business owner is required", "pt-br": "Pelo menos um sócio é obrigatório" },
  "atLeastOneUbo": { en: "At least one UBO is required", "pt-br": "Pelo menos um UBO é obrigatório" },
  "ownerRoleRequired": { en: "Owner role is required", "pt-br": "Função do sócio é obrigatória" },
  "ownerNameRequired": { en: "Owner name is required", "pt-br": "Nome do sócio é obrigatório" },
  "ownerDobRequired": { en: "Owner date of birth is required", "pt-br": "Data de nascimento do sócio é obrigatória" },
  "ownerEmailRequired": { en: "Owner email is required", "pt-br": "E-mail do sócio é obrigatório" },
  "ownerTaxIdRequired": { en: "Owner Tax ID is required", "pt-br": "CPF do sócio é obrigatório" },
  "ownerOwnershipRequired": { en: "Owner ownership % is required", "pt-br": "Participação % do sócio é obrigatória" },
  "ownerAddressRequired": { en: "Owner address is required", "pt-br": "Endereço do sócio é obrigatório" },
  "ownerCityRequired": { en: "Owner city is required", "pt-br": "Cidade do sócio é obrigatória" },
  "ownerStateRequired": { en: "Owner state is required", "pt-br": "Estado do sócio é obrigatório" },
  "ownerPostalCodeRequired": { en: "Owner postal code is required", "pt-br": "CEP do sócio é obrigatório" },
  "ownerIdDocRequired": { en: "Owner ID document is required", "pt-br": "Documento de identidade do sócio é obrigatório" },
  "ownerIdDocFrontRequired": { en: "Owner ID document front is required", "pt-br": "Frente do documento do sócio é obrigatória" },
  "ownerIdDocBackRequired": { en: "Owner ID document back is required", "pt-br": "Verso do documento do sócio é obrigatório" },
  "ownerProofAddressRequired": { en: "Owner proof of address is required", "pt-br": "Comprovante de endereço do sócio é obrigatório" },
  "incorporationDocRequired": { en: "Incorporation document is required", "pt-br": "Documento de constituição é obrigatório" },
  "proofOfOwnershipDocRequired": { en: "Proof of Ownership document is required", "pt-br": "Comprovante de propriedade é obrigatório" },
  "proofOfAddressDocRequired": { en: "Proof of Address document is required", "pt-br": "Comprovante de endereço é obrigatório" },
  "corporateStructureRequired": { en: "Corporate Structure Chart is required", "pt-br": "Organograma societário é obrigatório" },
  "balanceSheetRequired": { en: "Signed Balance Sheet is required", "pt-br": "Balanço patrimonial assinado é obrigatório" },
  "lastInvoiceRequired": { en: "Last Invoice is required", "pt-br": "Última invoice é obrigatória" },
  "invoiceTypeRequired": { en: "Invoice type is required", "pt-br": "Tipo de invoice é obrigatório" },
  "invoiceContractRequired": { en: "Invoice contract is required for service invoices", "pt-br": "Contrato é obrigatório para invoices de serviço" },
  "invoiceOrContractRequired": { en: "For service invoices, upload the invoice or the contract (at least one)", "pt-br": "Para invoices de serviço, envie a invoice ou o contrato (pelo menos um)" },
  "regulatedDetailsRequired": { en: "Regulated Activity Details is required", "pt-br": "Detalhes da atividade regulada são obrigatórios" },
  "sourceOfFundsRequired": { en: "Source of Funds is required", "pt-br": "Origem dos recursos é obrigatória" },
  "monthlyVolumeRequired": { en: "Monthly Volume is required", "pt-br": "Volume mensal é obrigatório" },
  "perTxVolumeRequired": { en: "Per-Transaction Volume is required", "pt-br": "Volume por transação é obrigatório" },
  "jurisdictionRequired": { en: "At least one jurisdiction is required", "pt-br": "Pelo menos uma jurisdição é obrigatória" },
  "activityRequired": { en: "At least one involved activity is required", "pt-br": "Pelo menos uma atividade envolvida é obrigatória" },
  "counterpartyRequired": { en: "At least one transaction counterparty is required", "pt-br": "Pelo menos uma contraparte é obrigatória" },

  // ── Admin ──
  "adminLogin": { en: "Admin Login", "pt-br": "Login Administrativo" },
  "signInToContinue": { en: "Sign in to continue", "pt-br": "Entre para continuar" },
  "username": { en: "Username", "pt-br": "Usuário" },
  "password": { en: "Password", "pt-br": "Senha" },
  "invalidCredentials": { en: "Invalid username or password", "pt-br": "Usuário ou senha inválidos" },
  "connectionError": { en: "Connection error", "pt-br": "Erro de conexão" },
  "signingIn": { en: "Signing in...", "pt-br": "Entrando..." },
  "signIn": { en: "Sign in", "pt-br": "Entrar" },
  "logout": { en: "Logout", "pt-br": "Sair" },
  "backups": { en: "Backups", "pt-br": "Backups" },
  "generateBackup": { en: "Generate backup", "pt-br": "Gerar backup" },
  "generating": { en: "Generating...", "pt-br": "Gerando..." },
  "noBackups": { en: "No backups yet", "pt-br": "Nenhum backup ainda" },
  "uploaded": { en: "uploaded", "pt-br": "enviado" },
  "uploadBackup": { en: "Upload backup", "pt-br": "Enviar backup" },
  "restore": { en: "Restore", "pt-br": "Restaurar" },
  "restoring": { en: "Restoring...", "pt-br": "Restaurando..." },
  "restoreConfirm": {
    en: "This will REPLACE the current database and uploaded files with the backup. The app will restart. Continue?",
    "pt-br": "Isso SUBSTITUIRÁ o banco de dados atual e os arquivos enviados pelo backup. O aplicativo será reiniciado. Continuar?",
  },
  "restoreStarted": {
    en: "Restore started. The app is restarting — refresh in a few seconds.",
    "pt-br": "Restauração iniciada. O aplicativo está reiniciando — atualize em alguns segundos.",
  },
  "delete": { en: "Delete", "pt-br": "Excluir" },
  "close": { en: "Close", "pt-br": "Fechar" },
  "adminCustomers": { en: "Admin — Customers", "pt-br": "Admin — Clientes" },
  "customersRegistered": { en: "customer(s) registered", "pt-br": "cliente(s) registrado(s)" },
  "noCustomersYet": { en: "No customers yet.", "pt-br": "Nenhum cliente ainda." },
  "back": { en: "Back", "pt-br": "Voltar" },
  "unnamed": { en: "Unnamed", "pt-br": "Sem nome" },
  "created": { en: "Created", "pt-br": "Criado" },
  "updated": { en: "Updated", "pt-br": "Atualizado" },
  "owners": { en: "Owners", "pt-br": "Sócios" },
  "docs": { en: "Docs", "pt-br": "Docs" },
  "customerNotFound": { en: "Customer not found", "pt-br": "Cliente não encontrado" },
  "noOwnersAdded": { en: "No owners added.", "pt-br": "Nenhum sócio adicionado." },
  "noIdDocUploaded": { en: "No ID document uploaded.", "pt-br": "Nenhum documento de identidade enviado." },
  "noProofOfAddressUploaded": { en: "No proof of address uploaded.", "pt-br": "Nenhum comprovante de endereço enviado." },
  "noDocumentsUploaded": { en: "No documents uploaded.", "pt-br": "Nenhum documento enviado." },

  // ── Admin Detail Sections ──
  "companyDataAdminDesc": { en: "Business details submitted by the client.", "pt-br": "Detalhes comerciais enviados pelo cliente." },
  "physicalAddressAdminDesc": { en: "Registered business address.", "pt-br": "Endereço comercial registrado." },
  "businessOwnersAdminDesc": { en: "Beneficial owners and controlling partners.", "pt-br": "Beneficiários finais e sócios controladores." },
  "documentsAdminDesc": { en: "Company-level documents submitted.", "pt-br": "Documentos da empresa enviados." },
  "financialInfoAdminDesc": { en: "Financial profile of the business.", "pt-br": "Perfil financeiro da empresa." },
  "sourceOfFundsAdminDesc": { en: "Source and flow of funds.", "pt-br": "Origem e fluxo dos recursos." },
  "regulatedActivity": { en: "Regulated Activity", "pt-br": "Atividade Regulada" },
  "noWebsitePrefix": { en: "No website", "pt-br": "Sem website" },
  "incorporationArticles": { en: "Incorporation Articles", "pt-br": "Contrato Social" },

  "completionLabel": { en: "Completion", "pt-br": "Preenchimento" },
  "fieldsFilled": { en: "fields filled", "pt-br": "campos preenchidos" },
  "requestMoreInfo": { en: "Request More Info", "pt-br": "Solicitar Mais Informações" },
  "requestMoreInfoDesc": { en: "Change the customer's status and optionally add a note. Notes are shown to the customer when status is Request.", "pt-br": "Altere o status do cliente e, opcionalmente, adicione uma nota. As notas são exibidas ao cliente quando o status é Solicitação." },
  "adminNotes": { en: "Notes for the customer", "pt-br": "Notas para o cliente" },
  "adminNotesPlaceholder": { en: "Please provide additional documents or correct the following information...", "pt-br": "Por favor, forneça documentos adicionais ou corrija as seguintes informações..." },
  "sendRequest": { en: "Send Request", "pt-br": "Enviar Solicitação" },
  "save": { en: "Save", "pt-br": "Salvar" },
  "cancel": { en: "Cancel", "pt-br": "Cancelar" },
  "statusUpdated": { en: "Status updated successfully", "pt-br": "Status atualizado com sucesso" },
  "statusUpdateFailed": { en: "Failed to update status", "pt-br": "Falha ao atualizar o status" },
  "adminNotesCard": { en: "Action Required", "pt-br": "Ação Necessária" },
  "request": { en: "Request", "pt-br": "Solicitação" },
  "openPublicForm": { en: "Open public form", "pt-br": "Abrir formulário público" },

  // ── Language Switcher ──
  "language": { en: "Language", "pt-br": "Idioma" },
} as const;

type TranslationKey = keyof typeof translations;

interface I18nContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: TranslationKey) => string;
}

const I18nContext = createContext<I18nContextType>({
  locale: "pt-br",
  setLocale: () => {},
  t: (key) => key,
});

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    try {
      const saved = localStorage.getItem("kyb_locale");
      if (saved === "en" || saved === "pt-br") return saved;
    } catch {}
    return "pt-br";
  });

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try { localStorage.setItem("kyb_locale", l); } catch {}
  }, []);

  const t = useCallback((key: TranslationKey): string => {
    const entry = translations[key];
    if (!entry) return key;
    return entry[locale] || entry["en"] || key;
  }, [locale]);

  return (
    <I18nContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}

export function LanguageSwitcher() {
  const { locale, setLocale } = useI18n();
  return (
    <div style={{
      position: "fixed",
      bottom: 0,
      left: 0,
      right: 0,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      gap: 8,
      padding: "10px 0",
      background: "#f9fafb",
      borderTop: "1px solid #e5e7eb",
      fontSize: 13,
      zIndex: 100,
    }}>
      <button
        onClick={() => setLocale("pt-br")}
        style={{
          background: "none",
          border: locale === "pt-br" ? "1px solid #111827" : "1px solid transparent",
          borderRadius: 6,
          padding: "4px 12px",
          cursor: "pointer",
          fontWeight: locale === "pt-br" ? 600 : 400,
          color: locale === "pt-br" ? "#111827" : "#6b7280",
          fontFamily: "inherit",
          fontSize: 13,
        }}
      >
        Português (BR)
      </button>
      <span style={{ color: "#d1d5db" }}>|</span>
      <button
        onClick={() => setLocale("en")}
        style={{
          background: "none",
          border: locale === "en" ? "1px solid #111827" : "1px solid transparent",
          borderRadius: 6,
          padding: "4px 12px",
          cursor: "pointer",
          fontWeight: locale === "en" ? 600 : 400,
          color: locale === "en" ? "#111827" : "#6b7280",
          fontFamily: "inherit",
          fontSize: 13,
        }}
      >
        English
      </button>
    </div>
  );
}
